/**
 * Component: Size Quiz
 * Breed-based product sizing calculator.
 * Reads config and product data from a <script type="application/json"> element,
 * calculates a size recommendation, and renders results inside the size guide dock-modal.
 *
 * The measurement schema is injected via config.measurements, making the quiz
 * product-type agnostic. Each measurement defines a role (primary, secondary,
 * tertiary) which drives the matching engine, scoring, and result branching.
 *
 * Wrapped in a load guard so that sections loading this script more than once
 * on the same page (e.g. multiple standalone quiz sections) do not redeclare
 * `SizeQuiz` or double-bind the auto-init listener.
 */
if (!window.SizeQuiz) {

class SizeQuiz {
  /** @type {Set<SizeQuiz>} */
  static #instances = new Set();
  /** @type {boolean} */
  static #listening = false;

  /** @type {HTMLElement} */
  #container;
  /** @type {Object} */
  #config;
  /** @type {Object} */
  #data;
  /** @type {Object} */
  #altData;
  /** @type {Object} */
  #altSizesCache = {};
  /** @type {Map<string, Promise<Array>>} Group sibling handle → live stock fetch */
  #groupSiblingStockPromises = new Map();
  /** @type {Map<string, Array>} Group sibling handle → resolved variants */
  #groupSiblingStock = new Map();
  /** @type {boolean} */
  #imperial = false;

  /* Measurement schema */
  /** @type {Map<string, Object>} Measurement key → config object */
  #measurements = new Map();
  /** @type {Map<string, HTMLInputElement>} Measurement key → input element */
  #measurementInputs = new Map();
  /** @type {Map<string, HTMLElement>} Measurement key → field group element */
  #measurementFields = new Map();
  /** @type {string|null} */
  #primaryKey = null;
  /** @type {string|null} */
  #secondaryKey = null;
  /** @type {string|null} */
  #tertiaryKey = null;
  /** @type {string} */
  #productTypeName = 'harness';

  /* DOM references */
  #form;
  #resultContainer;
  #submitBtn;
  #breedSelect;
  #weightInput;
  #ageSelect;
  #weightField;

  /* View-product-only mode (e.g. breed LP card carousel) — when true the result
     screen omits stock badges and per-size buttons, replacing them with a single
     "View product" link. Set from `data-quiz-view-product-only` on the root. */
  #viewProductOnly = false;
  #viewProductUrl = '';
  #helpUrl = '';

  /* Icon template references */
  #iconTemplates = {};

  /* Size-name helpers */
  #sizeLongForms = {
    XXS: ['XXSMALL'],
    XS: ['XSMALL'],
    S: ['SMALL'],
    M: ['MEDIUM'],
    L: ['LARGE'],
    XL: ['XLARGE', 'X-LARGE'],
    XXL: ['XXLARGE', 'XX-LARGE'],
  };

  constructor(container) {
    this.#container = container;

    const jsonEl = container.querySelector('script[type="application/json"]');
    if (!jsonEl) return;

    let payload;

    try {
      payload = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    this.#config = payload.config || {};
    this.#data = payload.product || {};
    this.#altData = payload.altData || {};
    this.#productTypeName = this.#config.productTypeName || 'harness';

    this.#initMeasurements();
    this.#cacheElements();
    this.#populateBreeds();
    this.#applyBreedPrefill();
    this.#bindEvents();
    this.#restoreUnitPreference();
    this.#updateFieldVisibility();
    this.#parseTableData();

    SizeQuiz.#prune();
    SizeQuiz.#instances.add(this);
    SizeQuiz.#ensureDocumentListener();
  }

  static #prune() {
    for (const instance of SizeQuiz.#instances) {
      if (!instance.#container.isConnected) {
        SizeQuiz.#instances.delete(instance);
      }
    }
  }

  static #ensureDocumentListener() {
    if (SizeQuiz.#listening) return;

    SizeQuiz.#listening = true;

    /* One document listener for all quizzes. Combined listing colour swaps
       can construct a new SizeQuiz while the size guide is open. */
    document.addEventListener('size-guide:unit-change', (e) => {
      SizeQuiz.#onDocumentUnitChange(e);
    });
  }

  static #onDocumentUnitChange(e) {
    if (!e.detail) return;

    SizeQuiz.#prune();

    const isImperial = Boolean(e.detail.imperial);

    for (const instance of SizeQuiz.#instances) {
      if (e.detail.source === instance) continue;
      if (isImperial === instance.#imperial) continue;

      instance.#setUnitPreference(isImperial, false);
    }
  }

  /* ==========================================================================
     Measurement schema
     ========================================================================== */

  #initMeasurements() {
    const measurements = this.#config.measurements;

    if (measurements && measurements.length) {
      for (const m of measurements) {
        this.#measurements.set(m.key, m);

        if (m.role === 'primary') this.#primaryKey = m.key;
        else if (m.role === 'secondary') this.#secondaryKey = m.key;
        else if (m.role === 'tertiary') this.#tertiaryKey = m.key;
      }
    } else {
      /* Legacy fallback — harness measurements */
      this.#primaryKey = 'chest';
      this.#secondaryKey = 'neck';
      this.#tertiaryKey = 'chestLength';

      this.#measurements.set('chest', {
        key: 'chest',
        role: 'primary',
        unit: 'length',
        displayName: 'chest',
        columnMatch: ['chest'],
        columnExclude: ['length'],
        placeholder: { metric: 'e.g. 70', imperial: 'e.g. 28' },
        inputRange: { metric: { min: 30, max: 150 }, imperial: { min: 12, max: 59 } },
        scoreWeight: 30,
        defaultEnabled: true,
        mismatchLabels: { tooSmall: 'smaller', tooLarge: 'larger', slightlySmall: 'smaller', slightlyLarge: 'larger' },
      });

      this.#measurements.set('neck', {
        key: 'neck',
        role: 'secondary',
        unit: 'length',
        displayName: 'neck',
        columnMatch: ['neck'],
        columnExclude: [],
        placeholder: { metric: 'e.g. 45', imperial: 'e.g. 18' },
        inputRange: { metric: { min: 15, max: 100 }, imperial: { min: 6, max: 40 } },
        scoreWeight: 20,
        defaultEnabled: true,
        mismatchLabels: { tooSmall: 'small', tooLarge: 'large', slightlySmall: 'smaller', slightlyLarge: 'larger' },
      });

      this.#measurements.set('chestLength', {
        key: 'chestLength',
        role: 'tertiary',
        unit: 'length',
        displayName: 'chest length',
        columnMatch: ['length', 'chest len'],
        columnExclude: [],
        placeholder: { metric: 'e.g. 35', imperial: 'e.g. 14' },
        inputRange: { metric: { min: 10, max: 80 }, imperial: { min: 4, max: 31 } },
        scoreWeight: 10,
        defaultEnabled: false,
        mismatchLabels: { tooSmall: 'short', tooLarge: 'long', slightlySmall: 'shorter', slightlyLarge: 'longer' },
      });
    }
  }

  /* ==========================================================================
     Element cache
     ========================================================================== */

  #cacheElements() {
    this.#form = this.#container.querySelector('.js-size-quiz-form');
    this.#resultContainer = this.#container.querySelector('.js-size-quiz-result');
    this.#submitBtn = this.#container.querySelector('.js-size-quiz-submit');
    this.#breedSelect = this.#container.querySelector('.js-quiz-breed');
    this.#weightInput = this.#container.querySelector('.js-quiz-weight');
    this.#ageSelect = this.#container.querySelector('.js-quiz-age');
    this.#weightField = this.#container.querySelector('.js-quiz-field-weight');

    this.#viewProductOnly = this.#container.dataset.quizViewProductOnly !== undefined;
    this.#viewProductUrl =
      this.#container.dataset.productUrl
      || (this.#data && this.#data.product && this.#data.product.url)
      || '';
    this.#helpUrl = this.#container.dataset.helpUrl || '';

    /* Cache measurement inputs dynamically from schema */
    for (const [key] of this.#measurements) {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      const input = this.#container.querySelector(`.js-quiz-${cssKey}`);

      if (input) this.#measurementInputs.set(key, input);

      const field = this.#container.querySelector(`.js-quiz-field-${cssKey}`);

      if (field) {
        this.#measurementFields.set(key, field);
      } else if (input) {
        const parentField = input.closest('.size-quiz__field');
        if (parentField) this.#measurementFields.set(key, parentField);
      }
    }

    /* Cache icon templates */
    for (const tpl of this.#container.querySelectorAll('template[data-quiz-icon]')) {
      this.#iconTemplates[tpl.dataset.quizIcon] = tpl.innerHTML;
    }
  }

  /* ==========================================================================
     Breed dropdown
     ========================================================================== */

  #populateBreeds() {
    if (!this.#breedSelect || !this.#config.breeds) return;

    for (const breed of this.#config.breeds) {
      const option = document.createElement('option');
      option.value = breed.name;
      option.textContent = breed.name;
      this.#breedSelect.appendChild(option);
    }
  }

  /* ==========================================================================
     Optional silent breed prefill (driven by data-quiz-prefill-breed on the
     container). When the supplied breed name matches a configured breed, the
     dropdown is preselected and its field is hidden. When it does not match,
     we fall back to "Mixed Breed" so newer breed landing pages (whose breed
     isn't yet in the quiz dataset) still work — the breed field stays hidden
     and the weight field is revealed instead.
     ========================================================================== */

  #applyBreedPrefill() {
    const prefill = this.#container.dataset.quizPrefillBreed;
    if (!prefill || !this.#breedSelect || !this.#config.breeds) return;

    const match = this.#config.breeds.find((breed) => breed.name === prefill);
    let selected = '';

    if (match) {
      selected = prefill;
    } else {
      const fallback = this.#config.breeds.find((breed) => breed.name === 'Mixed Breed');
      if (!fallback) return;
      selected = fallback.name;
    }

    this.#breedSelect.value = selected;

    const breedField = this.#breedSelect.closest('.size-quiz__field');
    if (breedField) breedField.classList.add('size-quiz__hidden');
  }

  /* ==========================================================================
     Event binding
     ========================================================================== */

  #bindEvents() {
    if (this.#submitBtn) {
      this.#submitBtn.addEventListener('click', () => {
        const formVisible = this.#form && !this.#form.classList.contains('size-quiz__hidden');

        if (formVisible) {
          this.#calculateResult();
        } else {
          this.#showForm();
        }
      });
    }

    if (this.#breedSelect) {
      this.#breedSelect.addEventListener('change', () => this.#updateWeightVisibility());
    }

    const inputs = [this.#breedSelect, this.#ageSelect, this.#weightInput, ...this.#measurementInputs.values()];

    for (const input of inputs) {
      if (!input) continue;
      input.addEventListener('change', () => this.#clearFieldError(input));
      input.addEventListener('input', () => this.#clearFieldError(input));
    }

    /* Prevent scroll-wheel from changing number inputs */
    for (const input of this.#container.querySelectorAll('input[type="number"]')) {
      input.addEventListener('wheel', (e) => {
        if (document.activeElement === input) {
          e.preventDefault();
        }
      }, { passive: false });
    }

    /* Unit toggle */
    for (const btn of this.#container.querySelectorAll('.js-quiz-unit-btn')) {
      btn.addEventListener('click', () => {
        this.#setUnitPreference(btn.dataset.unit === 'imperial', true);
      });
    }
  }

  /* ==========================================================================
     Unit conversion
     ========================================================================== */

  #restoreUnitPreference() {
    const stored = sessionStorage.getItem('sizeGuideImperial');

    if (stored === '1') {
      this.#imperial = true;
    }

    this.#applyUnits();
  }

  #setUnitPreference(isImperial, emit) {
    this.#imperial = isImperial;
    sessionStorage.setItem('sizeGuideImperial', isImperial ? '1' : '0');
    this.#applyUnits();

    if (emit) {
      document.dispatchEvent(
        new CustomEvent('size-guide:unit-change', {
          detail: { imperial: isImperial, source: this },
        }),
      );
    }
  }

  #applyUnits() {
    const isImperial = this.#imperial;

    for (const el of this.#container.querySelectorAll('.js-unit-suffix-length')) {
      el.textContent = isImperial ? 'in' : 'cm';
    }

    for (const el of this.#container.querySelectorAll('.js-unit-suffix-weight')) {
      el.textContent = isImperial ? 'lbs' : 'kg';
    }

    /* Apply measurement-specific placeholder/min/max from schema */
    for (const [key, m] of this.#measurements) {
      const input = this.#measurementInputs.get(key);
      if (!input) continue;

      const range = isImperial ? m.inputRange.imperial : m.inputRange.metric;
      const placeholder = isImperial ? m.placeholder.imperial : m.placeholder.metric;

      input.placeholder = placeholder;
      input.min = String(range.min);
      input.max = String(range.max);
    }

    if (this.#weightInput) {
      this.#weightInput.placeholder = isImperial ? 'e.g. 55' : 'e.g. 25';
      this.#weightInput.min = isImperial ? '2' : '1';
      this.#weightInput.max = isImperial ? '220' : '100';
    }

    for (const btn of this.#container.querySelectorAll('.js-quiz-unit-btn')) {
      btn.classList.toggle('size-quiz__unit-btn--active', btn.dataset.unit === (isImperial ? 'imperial' : 'metric'));
      btn.setAttribute('aria-pressed', btn.dataset.unit === (isImperial ? 'imperial' : 'metric') ? 'true' : 'false');
    }
  }

  #cmToIn(cm) {
    return +(cm / 2.54).toFixed(1);
  }

  #inToCm(inches) {
    return +(inches * 2.54).toFixed(1);
  }

  #kgToLb(kg) {
    return +(kg * 2.205).toFixed(1);
  }

  #lbToKg(lbs) {
    return +(lbs / 2.205).toFixed(1);
  }

  #fmtLen(cm) {
    return this.#imperial ? `${this.#cmToIn(cm)}"` : `${cm}cm`;
  }

  #fmtWt(kg) {
    return this.#imperial ? `${this.#kgToLb(kg)}lbs` : `${kg}kg`;
  }

  #fmtLenRange(lo, hi) {
    return this.#imperial ? `${this.#cmToIn(lo)}-${this.#cmToIn(hi)}"` : `${lo}-${hi}cm`;
  }

  #fmtWtRange(lo, hi) {
    return this.#imperial ? `${this.#kgToLb(lo)}-${this.#kgToLb(hi)}lbs` : `${lo}-${hi}kg`;
  }

  /* ==========================================================================
     Field visibility
     ========================================================================== */

  #updateFieldVisibility() {
    const productConfig = this.#getCurrentProductConfig();

    for (const [key, m] of this.#measurements) {
      const fieldGroup = this.#measurementFields.get(key);
      if (!fieldGroup) continue;

      const show = this.#isMeasurementEnabled(key, productConfig);
      fieldGroup.classList.toggle('size-quiz__hidden', !show);
    }

    this.#updateWeightVisibility();
  }

  #updateWeightVisibility() {
    if (!this.#weightField) return;
    const show = this.#requiresWeight();
    this.#weightField.classList.toggle('size-quiz__hidden', !show);

    /* Hide the " / kg" or " / lbs" suffix on the unit toggle when weight isn't a factor */
    for (const el of this.#container.querySelectorAll('.js-quiz-unit-weight')) {
      el.classList.toggle('size-quiz__hidden', !show);
    }

    if (!show && this.#weightInput) {
      this.#weightInput.value = '';
      this.#setFieldError(this.#weightInput, false);
    }
  }

  #requiresWeight() {
    if (!this.#breedSelect || !this.#breedSelect.value) return false;
    const bd = this.#getBreedData(this.#breedSelect.value);
    return !bd || bd.minWeight === null;
  }

  /**
   * Determines if a measurement is enabled for the current product configuration.
   * Secondary measurements default to enabled (opt-out via fields.key = false).
   * Tertiary measurements default to disabled (opt-in via fields.key = true).
   */
  #isMeasurementEnabled(key, productConfig) {
    const m = this.#measurements.get(key);
    if (!m) return false;
    if (m.role === 'primary') return true;

    if (m.defaultEnabled) {
      return !productConfig || !productConfig.fields || productConfig.fields[key] !== false;
    }

    return productConfig && productConfig.fields && productConfig.fields[key] === true;
  }

  /* ==========================================================================
     Size data parsing (row-based format)
     ========================================================================== */

  #identifyColumns(headings) {
    const result = { sizeIdx: -1 };

    for (const [key] of this.#measurements) {
      result[key] = -1;
    }

    for (let i = 0; i < headings.length; i++) {
      const h = headings[i].toLowerCase().trim();

      if (h === 'size' || h === 'sizes' || h.endsWith('size') || h.endsWith('sizes')) {
        result.sizeIdx = i;
        continue;
      }

      for (const [key, m] of this.#measurements) {
        if (result[key] !== -1) continue;

        const matches = (m.columnMatch || []).some((pattern) => h.indexOf(pattern) !== -1);
        const excluded = (m.columnExclude || []).some((pattern) => h.indexOf(pattern) !== -1);

        if (matches && !excluded) {
          result[key] = i;
          break;
        }
      }
    }

    return result;
  }

  #parseTableData() {
    if (this.#data._parsed) return;
    this.#data._parsed = true;

    const headings = this.#data.sizeChartHeadings;
    const rows = this.#data.sizeChartRows;
    if (!headings || !rows || !rows.length) return;

    const columns = this.#identifyColumns(headings);
    if (columns.sizeIdx === -1) return;

    this.#data.sizes = {};

    for (const row of rows) {
      const sizeName = row[columns.sizeIdx] ? row[columns.sizeIdx].trim() : '';
      if (!sizeName) continue;

      const sizeData = { name: sizeName };

      for (const [key] of this.#measurements) {
        sizeData[key] = null;

        if (columns[key] !== -1 && row[columns[key]]) {
          sizeData[key] = this.#parseRange(row[columns[key]]);
        }
      }

      this.#data.sizes[sizeName] = sizeData;
    }

    /* Compute product primary measurement range */
    const pk = this.#primaryKey;
    let minPrimary = Infinity;
    let maxPrimary = 0;

    for (const key of Object.keys(this.#data.sizes)) {
      const s = this.#data.sizes[key];

      if (s[pk]) {
        if (s[pk].min < minPrimary) minPrimary = s[pk].min;
        if (s[pk].max > maxPrimary) maxPrimary = s[pk].max;
      }
    }

    this.#data.productPrimaryRange = minPrimary !== Infinity ? { min: minPrimary, max: maxPrimary } : null;
  }

  #parseRange(str) {
    const cleaned = str.replace(/cm|in|"/gi, '').trim();
    const rangeMatch = cleaned.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);

    if (rangeMatch) {
      return { min: parseFloat(rangeMatch[1]), max: parseFloat(rangeMatch[2]) };
    }

    const singleMatch = cleaned.match(/(\d+(?:\.\d+)?)/);

    if (singleMatch) {
      const val = parseFloat(singleMatch[1]);
      /* Apply tolerance to single-value cells so small measurement variance
         doesn't fail the match. Size chart values are always metric. Tolerance
         defaults to 0.5 but can be overridden per-product-type via config. */
      const tol = typeof this.#config.singleValueTolerance === 'number' ? this.#config.singleValueTolerance : 0.5;
      return { min: val - tol, max: val + tol };
    }

    return null;
  }

  /* Parse alternative product size data */
  #parseAltSizes() {
    if (this.#altSizesCache._parsed) return;
    this.#altSizesCache._parsed = true;

    for (const refHandle of Object.keys(this.#altData)) {
      const product = this.#altData[refHandle];
      if (!product) continue;

      /* Alt data can have either row-based or column-based format */
      let sizes = {};

      if (product.sizeChartHeadings && product.sizeChartRows) {
        sizes = this.#parseAltRowBased(product.sizeChartHeadings, product.sizeChartRows);
      } else if (product.rawTableData) {
        sizes = this.#parseAltColumnBased(product.rawTableData);
      }

      if (!Object.keys(sizes).length) continue;

      /* Find the collection handle for this reference product */
      let collHandle = '';
      const collections = this.#getCollections();

      for (const coll of collections) {
        if (coll.referenceProduct === refHandle) {
          collHandle = coll.handle;
          break;
        }
      }

      if (collHandle) {
        this.#altSizesCache[collHandle] = {
          sizes,
          url: product.url,
          title: product.title,
          handle: product.handle,
          variants: product.variants || [],
          colour: product.colour,
          swatchColour: product.swatchColour,
          swatchImage: product.swatchImage,
          colourSiblings: product.colourSiblings || [],
        };
      }
    }
  }

  #parseAltRowBased(headings, rows) {
    const columns = this.#identifyColumns(headings);
    if (columns.sizeIdx === -1) return {};

    const sizes = {};

    for (const row of rows) {
      const sizeName = row[columns.sizeIdx] ? row[columns.sizeIdx].trim() : '';
      if (!sizeName) continue;

      const sizeData = { name: sizeName };

      for (const [key] of this.#measurements) {
        sizeData[key] = null;

        if (columns[key] !== -1 && row[columns[key]]) {
          sizeData[key] = this.#parseRange(row[columns[key]]);
        }
      }

      sizes[sizeName] = sizeData;
    }

    return sizes;
  }

  #parseAltColumnBased(rawTableData) {
    let tableData;

    try {
      tableData = typeof rawTableData === 'string' ? JSON.parse(rawTableData) : rawTableData;
    } catch {
      return {};
    }

    if (!tableData || !tableData.length) return {};

    const columns = [];
    const headers = [];

    for (let idx = 0; idx < tableData.length; idx++) {
      const parts = tableData[idx].split(',');
      columns.push(parts);
      headers.push(parts[0]);
    }

    const colMap = this.#identifyColumns(headers);
    if (colMap.sizeIdx === -1 || columns[colMap.sizeIdx].length < 2) return {};

    const sizes = {};
    const sizeCol = columns[colMap.sizeIdx];

    for (let i = 1; i < sizeCol.length; i++) {
      const sizeName = sizeCol[i].trim();
      if (!sizeName) continue;

      const sizeData = { name: sizeName };

      for (const [key] of this.#measurements) {
        sizeData[key] = null;

        if (colMap[key] !== -1 && columns[colMap[key]][i]) {
          sizeData[key] = this.#parseRange(columns[colMap[key]][i]);
        }
      }

      sizes[sizeName] = sizeData;
    }

    return sizes;
  }

  /* ==========================================================================
     Breed / product helpers
     ========================================================================== */

  #getBreedData(breedName) {
    if (!this.#config.breeds) return null;

    for (const breed of this.#config.breeds) {
      if (breed.name === breedName) return breed;
    }

    return null;
  }

  #getCollections() {
    return this.#config.productCollections || [];
  }

  #getCurrentCollectionHandle() {
    if (this.#data.product && this.#data.product.handle) {
      const productHandle = this.#data.product.handle.toLowerCase();
      const collections = this.#getCollections();
      let bestMatch = '';

      for (const coll of collections) {
        if (productHandle.indexOf(coll.handle) !== -1 && coll.handle.length > bestMatch.length) {
          bestMatch = coll.handle;
        }
      }

      if (bestMatch) return bestMatch;
    }

    const productRange = this.#data.productPrimaryRange;

    if (productRange) {
      const pk = this.#primaryKey;
      const minKey = `min${this.#capitalize(pk)}`;
      const maxKey = `max${this.#capitalize(pk)}`;
      const cols = this.#getCollections();

      for (const col of cols) {
        if (col[minKey] === productRange.min && col[maxKey] === productRange.max) {
          return col.handle;
        }
      }
    }

    return '';
  }

  #getCurrentProductConfig() {
    return this.#getCollectionConfig(this.#getCurrentCollectionHandle());
  }

  #getCollectionConfig(handle) {
    const collections = this.#getCollections();

    for (const coll of collections) {
      if (coll.handle === handle) return coll;
    }

    return null;
  }

  #sizeMatchesOption(option1, sizeName) {
    if (!option1 || !sizeName) return false;
    const o = option1.toUpperCase().trim();
    const s = sizeName.toUpperCase().trim();
    if (o === s) return true;

    const re = new RegExp(`(^|[^A-Z0-9/])${s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^A-Z0-9/])`);
    if (re.test(o)) return true;

    const longForms = this.#sizeLongForms[s];

    if (longForms) {
      for (const lf of longForms) {
        const lfRe = new RegExp(`(^|[^A-Z0-9/])${lf}($|[^A-Z0-9/])`);
        if (lfRe.test(o)) return true;
      }
    }

    for (const abbr of Object.keys(this.#sizeLongForms)) {
      for (const form of this.#sizeLongForms[abbr]) {
        if (s === form || s.indexOf(form) !== -1) {
          const abbrRe = new RegExp(`(^|[^A-Z0-9/])${abbr}($|[^A-Z0-9/])`);
          if (abbrRe.test(o)) return true;
        }
      }
    }

    return false;
  }

  /* ==========================================================================
     Validation
     ========================================================================== */

  #isFieldEmpty(el) {
    if (!el) return true;
    if (el.tagName === 'SELECT') return el.value === '';
    return el.value === '' || (el.type === 'number' && parseFloat(el.value) <= 0);
  }

  #setFieldError(el, show) {
    if (!el) return;
    const group = el.closest('.size-quiz__field');
    if (!group) return;

    if (show) {
      group.classList.add('size-quiz__field--error');

      if (!group.querySelector('.size-quiz__error-msg')) {
        const msg = document.createElement('span');
        msg.className = 'size-quiz__error-msg';
        msg.textContent = this.#t('field_required');
        group.appendChild(msg);
      }
    } else {
      group.classList.remove('size-quiz__field--error');
      const existing = group.querySelector('.size-quiz__error-msg');
      if (existing) existing.remove();
    }
  }

  #clearFieldError(el) {
    if (!el) return;
    if (!this.#isFieldEmpty(el)) this.#setFieldError(el, false);
  }

  #validateForm(showErrors) {
    const fields = [
      { el: this.#breedSelect, required: true },
      { el: this.#ageSelect, required: true },
      { el: this.#weightInput, required: this.#requiresWeight() },
    ];

    const productConfig = this.#getCurrentProductConfig();

    for (const [key, m] of this.#measurements) {
      const input = this.#measurementInputs.get(key);
      const required = m.role === 'primary' || this.#isMeasurementEnabled(key, productConfig);
      fields.push({ el: input, required });
    }

    let valid = true;

    for (const f of fields) {
      if (f.required && this.#isFieldEmpty(f.el)) {
        valid = false;
        if (showErrors) this.#setFieldError(f.el, true);
      } else if (showErrors) {
        this.#setFieldError(f.el, false);
      }
    }

    return valid;
  }

  /* ==========================================================================
     Weight / growth helpers
     ========================================================================== */

  #getWeightCategory(weight) {
    const ranges = this.#config.weightRanges || {};
    const order = this.#config.sizeOrder || ['XS', 'S', 'M', 'L', 'XL'];

    for (const cat of order) {
      if (ranges[cat] && weight >= ranges[cat].min && weight <= ranges[cat].max) {
        return cat;
      }
    }

    if (weight < (ranges.XS ? ranges.XS.min : 3)) return 'XS';
    if (weight > (ranges.XL ? ranges.XL.max : 60)) return 'XL';
    return 'M';
  }

  #getGrowthStage(weight, breedData, ageMonths) {
    const timeline = this.#config.growthTimeline;
    if (!timeline || !ageMonths) return 'unknown';

    const refWeight = breedData && breedData.maxWeight !== null ? breedData.maxWeight : weight;
    const sizeCategory = this.#getWeightCategory(refWeight);
    const growth = timeline[sizeCategory];
    if (!growth) return 'unknown';

    if (ageMonths < growth.growthMostlyComplete) return 'growing';
    if (ageMonths < growth.fullyMature) return 'almost-mature';
    return 'mature';
  }

  #formatBreedRange(range) {
    if (!range) return '';
    return `${range.min}-${range.max}`;
  }

  #isSizeInRange(size, range) {
    if (!range || !size) return true;
    const order = this.#config.sizeOrder || ['XS', 'S', 'M', 'L', 'XL'];
    const sizeIdx = order.indexOf(size);
    const minIdx = order.indexOf(range.min);
    const maxIdx = order.indexOf(range.max);

    if (sizeIdx === -1 || minIdx === -1 || maxIdx === -1) return true;
    return sizeIdx >= minIdx && sizeIdx <= maxIdx;
  }

  #getWeightRangeForSizes(range) {
    if (!range) return '';
    const ranges = this.#config.weightRanges || {};
    const minWeight = ranges[range.min] ? ranges[range.min].min : 0;
    const maxWeight = ranges[range.max] ? ranges[range.max].max : 0;
    return this.#fmtWtRange(minWeight, maxWeight);
  }

  /* ==========================================================================
     Stock checking
     ========================================================================== */

  #getVariantStock(sizeName) {
    const variants = this.#data.variants || [];

    for (const v of variants) {
      if (this.#sizeMatchesOption(v.option1, sizeName)) {
        return v.available;
      }
    }

    return null;
  }

  #fetchProductStock(productHandle) {
    return fetch(`/products/${encodeURIComponent(productHandle)}.js`)
      .then((res) => res.json())
      .then((product) => {
        const options = Array.isArray(product.options) ? product.options : [];
        let sizeIdx = 0;
        for (let i = 0; i < options.length; i++) {
          const name = String(options[i] && options[i].name != null ? options[i].name : options[i]).toLowerCase();
          if (!name.includes('color') && !name.includes('colour')) {
            sizeIdx = i;
            break;
          }
        }
        const variants = Array.isArray(product.variants) ? product.variants : [];
        return variants.map((v) => ({
          ...v,
          option1: Array.isArray(v.options) && v.options[sizeIdx] != null ? v.options[sizeIdx] : v.option1,
        }));
      })
      .catch(() => []);
  }

  /**
   * Live stock fetch for a single group sibling, cached per handle.
   * @param {string} handle
   * @returns {Promise<Array>}
   */
  #fetchGroupSiblingStock(handle) {
    if (this.#groupSiblingStockPromises.has(handle)) {
      return this.#groupSiblingStockPromises.get(handle);
    }

    const promise = this.#fetchProductStock(handle).then((variants) => {
      this.#groupSiblingStock.set(handle, variants);
      return variants;
    });

    this.#groupSiblingStockPromises.set(handle, promise);
    return promise;
  }

  /**
   * Walks group siblings (other quizable products sharing the same size chart)
   * looking for the first whose live stock confirms the given size is available.
   * Resolves with `{ handle, url, variantId, title }` or `null`.
   * @param {string} sizeName
   */
  #findGroupSiblingForSize(sizeName) {
    return this.#findAllGroupSiblingsForSize(sizeName).then((list) => (list.length ? list[0] : null));
  }

  /**
   * Walks group siblings in parallel and resolves with the full list of those
   * that have the given size in stock. Each entry: `{ handle, url, variantId,
   * title, colour, swatchColour, swatchImage }`. Order matches `groupSiblings`
   * declaration order in the JSON payload.
   * @param {string} sizeName
   * @returns {Promise<Array>}
   */
  #findAllGroupSiblingsForSize(sizeName) {
    const siblings = this.#data.groupSiblings || [];
    if (!siblings.length) return Promise.resolve([]);

    const lookups = siblings.map((sib) => {
      if (!sib || !sib.handle) return Promise.resolve(null);

      return this.#fetchGroupSiblingStock(sib.handle).then((variants) => {
        let match = null;

        for (const v of variants) {
          if (this.#sizeMatchesOption(v.option1, sizeName)) {
            match = v;
            break;
          }
        }

        if (!match || !match.available) return null;

        return {
          handle: sib.handle,
          url: sib.url,
          variantId: match.id,
          title: sib.title || '',
          colour: sib.colour || sib.title || '',
          swatchColour: sib.swatchColour || '',
          swatchImage: sib.swatchImage || null,
        };
      });
    });

    return Promise.all(lookups).then((results) => results.filter(Boolean));
  }

  #checkColourSiblingStock(sizeName) {
    const siblings = this.#data.colourSiblings || [];
    const currentColour = (this.#data.product && this.#data.product.colour) || '';
    const otherColours = siblings.filter((s) => s.colour !== currentColour);
    if (!otherColours.length) return Promise.resolve([]);

    const promises = [];

    for (const sibling of otherColours) {
      let siblingVariantId = null;

      for (const sv of sibling.variants || []) {
        if (this.#sizeMatchesOption(sv.option1, sizeName)) {
          siblingVariantId = sv.id;
          break;
        }
      }

      promises.push(
        this.#fetchProductStock(sibling.handle)
          .then((variants) => {
            let match = null;

            for (const v of variants) {
              if (this.#sizeMatchesOption(v.option1, sizeName)) {
                match = v;
                break;
              }
            }

            return {
              colour: sibling.colour,
              url: sibling.url,
              available: match ? match.available : false,
              swatchColour: sibling.swatchColour || '',
              swatchImage: sibling.swatchImage || null,
              variantId: siblingVariantId,
            };
          })
          .catch(() => ({
            colour: sibling.colour,
            url: sibling.url,
            available: false,
            swatchColour: sibling.swatchColour || '',
            swatchImage: sibling.swatchImage || null,
            variantId: siblingVariantId,
          }))
      );
    }

    return Promise.all(promises);
  }

  #checkAltColourSiblingStock(refHandle, currentHandle, sizeName) {
    const altRefData = this.#altData[refHandle];
    if (!altRefData) return Promise.resolve([]);

    const candidates = [];

    for (const s of altRefData.colourSiblings || []) {
      if (s.handle !== currentHandle) candidates.push(s);
    }

    if (currentHandle !== refHandle) {
      candidates.push({
        handle: altRefData.handle,
        colour: altRefData.colour || 'Black',
        url: altRefData.url,
        swatchColour: altRefData.swatchColour || '',
        swatchImage: altRefData.swatchImage || null,
        variants: altRefData.variants || [],
      });
    }

    if (!candidates.length) return Promise.resolve([]);

    const promises = [];

    for (const sibling of candidates) {
      let siblingVariantId = null;

      for (const sv of sibling.variants || []) {
        if (this.#sizeMatchesOption(sv.option1, sizeName)) {
          siblingVariantId = sv.id;
          break;
        }
      }

      promises.push(
        this.#fetchProductStock(sibling.handle)
          .then((variants) => {
            let match = null;

            for (const v of variants) {
              if (this.#sizeMatchesOption(v.option1, sizeName)) {
                match = v;
                break;
              }
            }

            return {
              colour: sibling.colour,
              url: sibling.url,
              available: match ? match.available : false,
              swatchColour: sibling.swatchColour || '',
              swatchImage: sibling.swatchImage || null,
              variantId: siblingVariantId,
            };
          })
          .catch(() => ({
            colour: sibling.colour,
            url: sibling.url,
            available: false,
            swatchColour: sibling.swatchColour || '',
            swatchImage: sibling.swatchImage || null,
            variantId: siblingVariantId,
          }))
      );
    }

    return Promise.all(promises);
  }

  /* ==========================================================================
     Alternative product helpers
     ========================================================================== */

  #findSizeForAlt(collHandle, vals) {
    this.#parseAltSizes();
    const altInfo = this.#altSizesCache[collHandle];
    if (!altInfo || !altInfo.sizes) return null;

    const collConfig = this.#getCollectionConfig(collHandle);
    const pk = this.#primaryKey;

    for (const sizeName of Object.keys(altInfo.sizes)) {
      const s = altInfo.sizes[sizeName];

      if (!s[pk] || vals[pk] < s[pk].min || vals[pk] > s[pk].max) continue;

      let fits = true;

      for (const [key, m] of this.#measurements) {
        if (m.role === 'primary') continue;

        const val = vals[key];
        if (val === null || val === undefined || !s[key]) continue;

        const enabled = this.#isMeasurementEnabled(key, collConfig);
        if (!enabled) continue;

        if (val < s[key].min || val > s[key].max) {
          fits = false;
          break;
        }
      }

      if (!fits) continue;

      return sizeName;
    }

    return null;
  }

  #findAltVariantForSize(collHandle, sizeName) {
    this.#parseAltSizes();
    const altInfo = this.#altSizesCache[collHandle];
    if (!altInfo || !altInfo.variants) return null;

    for (const v of altInfo.variants) {
      if (this.#sizeMatchesOption(v.option1, sizeName)) return v;
    }

    return null;
  }

  #findDirectedAlternatives(direction, vals) {
    this.#parseAltSizes();
    const currentHandle = this.#getCurrentCollectionHandle();
    const rules = this.#config.directedAlternatives || {};
    const currentRules = rules[currentHandle];
    if (!currentRules) return [];

    const candidates = direction === 'tooSmall' ? currentRules.tooSmall || [] : currentRules.tooBig || [];
    if (!candidates.length) return [];

    const collections = this.#getCollections();

    for (const candidateHandle of candidates) {
      let coll = null;

      for (const c of collections) {
        if (c.handle === candidateHandle) {
          coll = c;
          break;
        }
      }

      if (!coll) continue;

      /* Check each measurement's overall range on the collection */
      let fits = true;

      for (const [key, m] of this.#measurements) {
        const val = vals[key];
        if (val === null || val === undefined) continue;

        if (m.role !== 'primary') {
          const enabled = this.#isMeasurementEnabled(key, coll);
          if (!enabled) continue;
        }

        const minKey = `min${this.#capitalize(key)}`;
        const maxKey = `max${this.#capitalize(key)}`;

        if (coll[minKey] && coll[maxKey]) {
          if (val < coll[minKey] || val > coll[maxKey]) {
            fits = false;
            break;
          }
        }
      }

      if (!fits) continue;

      const fittingSize = this.#findSizeForAlt(coll.handle, vals);
      if (!fittingSize) continue;

      const altInfo = this.#altSizesCache[coll.handle];
      const productUrl = altInfo ? altInfo.url : `/collections/${coll.handle}`;
      const productHandle = altInfo ? altInfo.handle : null;
      const variant = this.#findAltVariantForSize(coll.handle, fittingSize);

      return [
        {
          handle: coll.handle,
          name: coll.name,
          fittingSize,
          productUrl,
          productHandle,
          variantId: variant ? variant.id : null,
          available: variant ? variant.available : null,
        },
      ];
    }

    return [];
  }

  #colourMatchAlternative(alt) {
    if (!alt.productHandle) return;
    alt.refHandle = alt.productHandle;

    const altRefData = this.#altData[alt.refHandle];
    alt.displayColour = (altRefData && altRefData.colour) || 'Black';
    alt.displaySwatchColour = (altRefData && altRefData.swatchColour) || '#000';
    alt.displaySwatchImage = (altRefData && altRefData.swatchImage) || null;

    const currentColour = (this.#data.product && this.#data.product.colour) || '';
    if (!currentColour || !altRefData || !altRefData.colourSiblings) return;

    if (altRefData.colour && altRefData.colour.toLowerCase() === currentColour.toLowerCase()) return;

    for (const sib of altRefData.colourSiblings) {
      if (sib.colour && sib.colour.toLowerCase() === currentColour.toLowerCase()) {
        alt.productHandle = sib.handle;
        alt.productUrl = sib.url;
        alt.displayColour = sib.colour;
        alt.displaySwatchColour = sib.swatchColour || '#ccc';
        alt.displaySwatchImage = sib.swatchImage || null;

        if (alt.fittingSize) {
          for (const v of sib.variants || []) {
            if (this.#sizeMatchesOption(v.option1, alt.fittingSize)) {
              alt.variantId = v.id;
              break;
            }
          }
        }

        return;
      }
    }
  }

  /* ==========================================================================
     Core calculation
     ========================================================================== */

  #calculateResult() {
    if (!this.#validateForm(true)) return;

    const pk = this.#primaryKey;
    const sk = this.#secondaryKey;
    const tk = this.#tertiaryKey;
    const skConfig = sk ? this.#measurements.get(sk) : null;
    const tkConfig = tk ? this.#measurements.get(tk) : null;
    const primaryName = this.#getMeasurementDisplayName(pk);
    const secondaryName = sk ? this.#getMeasurementDisplayName(sk) : '';
    const tertiaryName = tk ? this.#getMeasurementDisplayName(tk) : '';
    const typeName = this.#productTypeName;
    const typeNameCap = typeName ? typeName.charAt(0).toUpperCase() + typeName.slice(1) : '';

    const breed = this.#breedSelect.value;
    const ageMonths = this.#ageSelect && this.#ageSelect.value ? parseInt(this.#ageSelect.value, 10) : null;
    let weight = this.#weightInput && this.#weightInput.value ? parseFloat(this.#weightInput.value) : null;

    /* Read measurement values from inputs */
    const vals = {};

    for (const [key] of this.#measurements) {
      const input = this.#measurementInputs.get(key);
      vals[key] = input && input.value ? parseFloat(input.value) : null;
    }

    const breedData = this.#getBreedData(breed);

    let weightDerived = false;

    if (weight === null || isNaN(weight)) {
      if (breedData && breedData.maxWeight !== null) {
        weight = breedData.maxWeight;
        weightDerived = true;
      } else {
        weight = 0;
      }
    }

    /* Convert imperial → metric for internal logic */
    if (this.#imperial) {
      for (const [key, m] of this.#measurements) {
        if (vals[key] !== null && m.unit === 'length') {
          vals[key] = this.#inToCm(vals[key]);
        }
      }

      if (!weightDerived && weight > 0) weight = this.#lbToKg(weight);
    }

    /* Convenience aliases for the three measurement roles */
    const primary = vals[pk];
    const secondary = sk ? vals[sk] : null;
    const tertiary = tk ? vals[tk] : null;

    const growthStage = this.#getGrowthStage(weight, breedData, ageMonths);
    const sizes = this.#data.sizes || {};
    const sizeNames = Object.keys(sizes);

    if (!sizeNames.length) {
      this.#showResult({ type: 'error', title: this.#t('result_unable'), message: this.#t('result_unable_message') });
      return;
    }

    /* Puppy detection */
    let isPuppy = false;
    const puppyCfg = this.#config.puppyConfig;

    if (puppyCfg && puppyCfg.enabled) {
      if (weightDerived) {
        if (ageMonths && ageMonths <= 6) isPuppy = true;
      } else {
        if (ageMonths && ageMonths <= 6 && weight < puppyCfg.minWeight) {
          isPuppy = true;
        } else if (weight < puppyCfg.minWeight && (!ageMonths || ageMonths < 18)) {
          if (breedData && breedData.minWeight !== null) {
            isPuppy = weight < breedData.minWeight;
          }
        }
      }
    }

    if (isPuppy) {
      this.#showResult({
        type: 'warning',
        title: this.#t('result_puppy_growing'),
        message: `${this.#t('puppy_message')} ${this.#t('msg_puppy_too_small', { type_name: typeName })}`,
        contactFallback: true,
      });
      return;
    }

    /* Growth notice */
    let growthNotice = '';

    if (growthStage === 'growing') {
      growthNotice = this.#t('growth_growing');
    } else if (growthStage === 'almost-mature') {
      growthNotice = this.#t('growth_almost');
    }

    const weightNote = weightDerived ? '' : ` | ${this.#t('note_weight', { weight: this.#fmtWt(weight) })}`;

    /* Weight out-of-breed-range check */
    let weightOutOfBreedRange = false;
    let weightRangeMessage = '';

    if (!weightDerived && breedData && breedData.minWeight !== null && breedData.maxWeight !== null) {
      const tolerance = 0.2;
      const minTol = breedData.minWeight * (1 - tolerance);
      const maxTol = breedData.maxWeight * (1 + tolerance);

      if (weight < minTol || weight > maxTol) {
        weightOutOfBreedRange = true;
        weightRangeMessage = this.#t('msg_weight_out_of_range', { weight: this.#fmtWt(weight), breed, range: this.#fmtWtRange(breedData.minWeight, breedData.maxWeight) });
      }
    }

    /* Find matching sizes (primary measurement) */
    let primaryMatch = null;
    let primaryMatchName = null;
    const allPerfectFits = [];
    const primaryMatches = [];

    for (const sizeName of sizeNames) {
      const sizeData = sizes[sizeName];

      if (sizeData[pk] && primary >= sizeData[pk].min && primary <= sizeData[pk].max) {
        primaryMatches.push({ name: sizeName, data: sizeData });
      }
    }

    /* Comfort buffer: a measurement within `comfortBuffer` cm of a size's max is
       treated as too tight (chafe risk), so the fit is nudged up a size rather
       than recommended at the edge of the range. */
    const comfortBuffer = this.#config.comfortBuffer != null ? this.#config.comfortBuffer : 0;
    let sizedUpForComfort = false;
    let comfortAdvisory = false;
    const comfortSnug = [];

    if (comfortBuffer > 0 && primaryMatches.length > 0) {
      const snugMeasurements = (data) => {
        const snug = [];

        if (data[pk] && data[pk].max - primary <= comfortBuffer) snug.push(primaryName);

        if (
          sk &&
          data[sk] &&
          secondary !== null &&
          secondary >= data[sk].min &&
          secondary <= data[sk].max &&
          data[sk].max - secondary <= comfortBuffer
        ) {
          snug.push(secondaryName);
        }

        return snug;
      };

      const comfortableMatches = primaryMatches.filter((m) => snugMeasurements(m.data).length === 0);

      if (comfortableMatches.length === 0) {
        /* No roomier size available — recommend the least tight fit and flag it. */
        const roomiest = primaryMatches.reduce((a, b) => (b.data[pk].max > a.data[pk].max ? b : a));

        comfortAdvisory = true;
        comfortSnug.push(...snugMeasurements(roomiest.data));
        primaryMatches.length = 0;
        primaryMatches.push(roomiest);
      } else if (comfortableMatches.length < primaryMatches.length) {
        const droppedSmallest = this.#sortSizes(primaryMatches.map((m) => m.name), sizes)[0];
        const snug = snugMeasurements(sizes[droppedSmallest]);

        if (snug.length > 0 && !comfortableMatches.some((m) => m.name === droppedSmallest)) {
          sizedUpForComfort = true;
          comfortSnug.push(...snug);
        }

        primaryMatches.length = 0;
        primaryMatches.push(...comfortableMatches);
      }
    }

    if (primaryMatches.length > 0) {
      if (primaryMatches.length === 1) {
        primaryMatch = primaryMatches[0].data;
        primaryMatchName = primaryMatches[0].name;

        let isPerfect = true;

        if (sk && primaryMatch[sk] && secondary !== null) {
          if (secondary < primaryMatch[sk].min || secondary > primaryMatch[sk].max) isPerfect = false;
        }

        if (tk && primaryMatch[tk] && tertiary !== null) {
          if (tertiary < primaryMatch[tk].min || tertiary > primaryMatch[tk].max) isPerfect = false;
        }

        if (isPerfect) allPerfectFits.push(primaryMatchName);
      } else {
        let bestMatch = null;
        let bestScore = -Infinity;

        for (const match of primaryMatches) {
          let score = 0;
          let isPerfectFit = true;

          /* Score secondary measurement */
          if (sk && match.data[sk] && secondary !== null) {
            if (secondary >= match.data[sk].min && secondary <= match.data[sk].max) {
              score += 100;
              const mid = (match.data[sk].min + match.data[sk].max) / 2;
              const dist = Math.abs(secondary - mid);
              const range = (match.data[sk].max - match.data[sk].min) / 2;
              const weight = skConfig ? skConfig.scoreWeight : 20;
              score += range > 0 ? (1 - dist / range) * weight : weight;
            } else {
              isPerfectFit = false;
              const off = secondary < match.data[sk].min ? match.data[sk].min - secondary : secondary - match.data[sk].max;
              score -= off * 10;
            }
          }

          /* Score tertiary measurement */
          if (tk && match.data[tk] && tertiary !== null) {
            if (tertiary >= match.data[tk].min && tertiary <= match.data[tk].max) {
              score += 50;
            } else {
              isPerfectFit = false;
              const off =
                tertiary < match.data[tk].min
                  ? match.data[tk].min - tertiary
                  : tertiary - match.data[tk].max;
              score -= off * 10;
            }
          }

          /* Score primary closeness to midpoint */
          const primaryMid = (match.data[pk].min + match.data[pk].max) / 2;
          const primaryDist = Math.abs(primary - primaryMid);
          const primaryRange = (match.data[pk].max - match.data[pk].min) / 2;
          score += primaryRange > 0 ? (1 - primaryDist / primaryRange) * 10 : 10;

          if (isPerfectFit) allPerfectFits.push(match.name);

          if (score > bestScore) {
            bestScore = score;
            bestMatch = match;
          }
        }

        if (bestMatch) {
          primaryMatch = bestMatch.data;
          primaryMatchName = bestMatch.name;
        }
      }
    }

    /* No primary match — out of range */
    if (!primaryMatch) {
      let minP = Infinity;
      let maxP = 0;

      for (const sName of sizeNames) {
        const sData = sizes[sName];
        if (!sData[pk]) continue;
        if (sData[pk].min < minP) minP = sData[pk].min;
        if (sData[pk].max > maxP) maxP = sData[pk].max;
      }

      if (primary < minP) {
        const smallerAlts = this.#findDirectedAlternatives('tooSmall', vals);
        const hCfg = this.#getCurrentProductConfig();
        const currentProductName = hCfg ? hCfg.name : `this ${typeName}`;

        if (smallerAlts.length > 0) {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_too_small', { type_name: typeNameCap }),
            message: this.#t('msg_below_range_alt', { type_name: typeName }),
            alternatives: smallerAlts,
            growthNotice,
            flexiCta: true,
          });
        } else {
          this.#showResult({
            type: 'error',
            title: this.#t('result_too_small', { type_name: typeNameCap }),
            message: this.#t('msg_below_range', { product_name: currentProductName }),
            contactFallback: true,
            growthNotice,
            flexiCta: true,
          });
        }

        return;
      }

      if (primary > maxP) {
        const largerAlts = this.#findDirectedAlternatives('tooBig', vals);

        if (largerAlts.length > 0) {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_too_large', { type_name: typeNameCap }),
            message: this.#t('msg_exceed_range_alt', { type_name: typeName }),
            alternatives: largerAlts,
            growthNotice,
            flexiCta: true,
          });
        } else {
          this.#showResult({
            type: 'error',
            title: this.#t('result_too_large', { type_name: typeNameCap }),
            message: this.#t('msg_exceed_range', { type_name: typeName }),
            contactFallback: true,
            growthNotice,
            flexiCta: true,
          });
        }

        return;
      }

      /* Generic no match */
      const noMatchDir = primary <= (minP + maxP) / 2 ? 'tooSmall' : 'tooBig';
      const noMatchAlts = this.#findDirectedAlternatives(noMatchDir, vals);

      if (noMatchAlts.length > 0) {
        this.#showResult({
          type: 'warning',
          title: this.#t('result_no_size'),
          message: this.#t('msg_no_match_alt', { type_name: typeName }),
          alternatives: noMatchAlts,
          growthNotice,
        });
      } else {
        this.#showResult({
          type: 'error',
          title: this.#t('result_no_size'),
          message: this.#t('msg_no_match', { type_name: typeName }),
          contactFallback: true,
          growthNotice,
        });
      }

      return;
    }

    /* Secondary measurement fit check */
    let secondaryFits = true;
    let secondaryIssue = null;

    if (sk && primaryMatch[sk] && secondary !== null) {
      if (secondary < primaryMatch[sk].min) {
        secondaryFits = false;
        secondaryIssue = 'small';
      } else if (secondary > primaryMatch[sk].max) {
        secondaryFits = false;
        secondaryIssue = 'large';
      }
    }

    let secondaryMismatch = 0;

    if (!secondaryFits && primaryMatch[sk]) {
      secondaryMismatch =
        secondaryIssue === 'small' ? primaryMatch[sk].min - secondary : secondary - primaryMatch[sk].max;
    }

    /* Tertiary measurement fit check */
    let tertiaryFits = true;
    let tertiaryIssue = null;
    let tertiaryMismatch = 0;

    if (tk && primaryMatch[tk] && tertiary !== null) {
      if (tertiary < primaryMatch[tk].min) {
        tertiaryFits = false;
        tertiaryIssue = 'small';
        tertiaryMismatch = primaryMatch[tk].min - tertiary;
      } else if (tertiary > primaryMatch[tk].max) {
        tertiaryFits = false;
        tertiaryIssue = 'large';
        tertiaryMismatch = tertiary - primaryMatch[tk].max;
      }
    }

    /* Breed allowed products check */
    const allowedProducts =
      breedData && breedData.allowedProducts && breedData.allowedProducts.length > 0
        ? breedData.allowedProducts
        : null;

    let productNotAllowed = false;

    if (allowedProducts) {
      const currentColl = this.#getCurrentCollectionHandle();
      const currentCfg = this.#getCurrentProductConfig();

      if (currentColl && !allowedProducts.includes(currentColl) && !(currentCfg && currentCfg.noBreedRestriction)) {
        productNotAllowed = true;
      }
    }

    const currentProductConfig = this.#getCurrentProductConfig();
    const productRange = currentProductConfig && currentProductConfig.breedRange ? currentProductConfig.breedRange : null;
    let dogSizeInRange = true;
    const weightBasedSize = this.#getWeightCategory(weight);

    if (productRange && weightBasedSize && !allowedProducts) {
      dogSizeInRange = this.#isSizeInRange(weightBasedSize, productRange);
    }

    const threshold = this.#config.mismatchThreshold != null ? this.#config.mismatchThreshold : 5;

    /* Build note text dynamically from measurements */
    let noteText = `${this.#capitalize(primaryName)}: ${this.#fmtLen(primary)}`;

    if (sk) {
      noteText += ` | ${this.#capitalize(secondaryName)}: ${secondary !== null ? this.#fmtLen(secondary) : this.#t('note_na')}`;
    }

    if (tk) {
      noteText += ` | ${this.#capitalize(tertiaryName)}: ${tertiary !== null ? this.#fmtLen(tertiary) : this.#t('note_na')}`;
    }

    noteText += weightNote;

    /* === Result branching === */

    if (productNotAllowed) {
      const breedSmall = this.#findDirectedAlternatives('tooSmall', vals);
      const breedBig = this.#findDirectedAlternatives('tooBig', vals);
      let breedAlts = breedSmall.concat(breedBig).filter((a) => allowedProducts.includes(a.handle));
      const seen = {};
      breedAlts = breedAlts.filter((a) => {
        if (seen[a.handle]) return false;
        seen[a.handle] = true;
        return true;
      });
      breedAlts = breedAlts.slice(0, 1);

      const result = {
        type: 'warning',
        title: this.#t('result_not_ideal', { type_name: typeNameCap }),
        note: noteText,
        growthNotice,
      };

      if (primaryMatchName) {
        result.size = primaryMatchName;
        result.message = this.#t('msg_breed_not_recommended_size', { type_name: typeName, breed, size: primaryMatchName });
        result.selectVariant = primaryMatchName;
      } else {
        result.message = this.#t('msg_breed_not_recommended', { type_name: typeName, breed });
      }

      if (breedAlts.length > 0) {
        result.alternatives = breedAlts;
      } else {
        result.contactFallback = true;
      }

      this.#showResult(result);
      return;
    }

    if (secondaryMismatch > threshold && sk) {
      const tooSmallLabel = skConfig && skConfig.mismatchLabels ? skConfig.mismatchLabels.tooSmall : 'small';
      const tooLargeLabel = skConfig && skConfig.mismatchLabels ? skConfig.mismatchLabels.tooLarge : 'large';
      const issueLabel = secondaryIssue === 'large' ? tooLargeLabel : tooSmallLabel;

      let secondaryFitsAnother = false;

      for (const sn of sizeNames) {
        const nkSize = sizes[sn];

        if (nkSize[sk] && secondary >= nkSize[sk].min && secondary <= nkSize[sk].max) {
          secondaryFitsAnother = true;
          break;
        }
      }

      if (secondaryFitsAnother) {
        const mismatchDir = secondaryIssue === 'large' ? 'tooBig' : 'tooSmall';
        const alts = this.#findDirectedAlternatives(mismatchDir, vals);

        if (alts.length > 0) {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_check_measurements'),
            message: this.#t('msg_secondary_mismatch_alt', { secondary_name: secondaryName, secondary_value: this.#fmtLen(secondary), issue: issueLabel, primary_value: this.#fmtLen(primary), primary_name: primaryName }),
            alternatives: alts,
            growthNotice,
          });
        } else {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_check_measurements'),
            message: this.#t('msg_secondary_mismatch', { secondary_name: secondaryName, secondary_value: this.#fmtLen(secondary), issue: issueLabel, primary_value: this.#fmtLen(primary), primary_name: primaryName }),
            contactFallback: true,
            growthNotice,
          });
        }
      } else {
        const secDir = secondaryIssue === 'large' ? 'tooBig' : 'tooSmall';
        const secAlts = this.#findDirectedAlternatives(secDir, vals);

        if (secAlts.length > 0) {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_neck_oor', { type_name: typeNameCap }),
            message: this.#t('msg_secondary_oor_alt', { secondary_name: secondaryName, secondary_value: this.#fmtLen(secondary), issue: issueLabel, type_name: typeName }),
            alternatives: secAlts,
            growthNotice,
          });
        } else {
          this.#showResult({
            type: 'error',
            title: this.#t('result_measurements_oor'),
            message: this.#t('msg_all_oor', { type_name: typeName }),
            contactFallback: true,
            growthNotice,
          });
        }
      }

      return;
    }

    if (!dogSizeInRange) {
      const sizeOrder = this.#config.sizeOrder || ['XXS', 'XS', 'S', 'M', 'L', 'XL'];
      const dogIdx = sizeOrder.indexOf(weightBasedSize);
      const minIdx = sizeOrder.indexOf(productRange.min);
      const sizeDir = dogIdx < minIdx ? 'tooSmall' : 'tooBig';
      const sizeAlts = this.#findDirectedAlternatives(sizeDir, vals);

      if (sizeAlts.length > 0) {
        this.#showResult({
          type: 'warning',
          title: this.#t('result_not_right', { type_name: typeNameCap }),
          size: primaryMatchName,
          message: this.#t('msg_size_range_alt', { type_name: typeName, breed_range: this.#formatBreedRange(productRange), weight_range: this.#getWeightRangeForSizes(productRange), breed, dog_size: weightBasedSize }),
          note: noteText,
          alternatives: sizeAlts,
          growthNotice,
        });
      } else {
        this.#showResult({
          type: 'warning',
          title: this.#t('result_not_right', { type_name: typeNameCap }),
          size: primaryMatchName,
          message: this.#t('msg_size_range', { type_name: typeName, breed_range: this.#formatBreedRange(productRange), weight_range: this.#getWeightRangeForSizes(productRange), breed, dog_size: weightBasedSize }),
          note: noteText,
          contactFallback: true,
          growthNotice,
        });
      }

      return;
    }

    if (!tertiaryFits && tk) {
      const tooSmallLabel = tkConfig && tkConfig.mismatchLabels ? tkConfig.mismatchLabels.tooSmall : 'short';
      const tooLargeLabel = tkConfig && tkConfig.mismatchLabels ? tkConfig.mismatchLabels.tooLarge : 'long';
      const slightlySmallLabel = tkConfig && tkConfig.mismatchLabels ? tkConfig.mismatchLabels.slightlySmall : 'shorter';
      const slightlyLargeLabel = tkConfig && tkConfig.mismatchLabels ? tkConfig.mismatchLabels.slightlyLarge : 'longer';
      const issueLabel = tertiaryIssue === 'large' ? tooLargeLabel : tooSmallLabel;
      const slightlyLabel = tertiaryIssue === 'large' ? slightlyLargeLabel : slightlySmallLabel;

      if (tertiaryMismatch > threshold) {
        const lenDir = tertiaryIssue === 'small' ? 'tooSmall' : 'tooBig';
        const lenAlts = this.#findDirectedAlternatives(lenDir, vals);

        if (lenAlts.length > 0) {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_chest_length_oor', { type_name: typeNameCap }),
            size: primaryMatchName,
            message: this.#t('msg_tertiary_oor_alt', { tertiary_name: tertiaryName, tertiary_value: this.#fmtLen(tertiary), issue: issueLabel, size: primaryMatchName, range: this.#fmtLenRange(primaryMatch[tk].min, primaryMatch[tk].max) }),
            note: noteText,
            alternatives: lenAlts,
            growthNotice,
          });
        } else {
          this.#showResult({
            type: 'warning',
            title: this.#t('result_chest_length_oor', { type_name: typeNameCap }),
            message: this.#t('msg_tertiary_oor', { tertiary_name: tertiaryName, tertiary_value: this.#fmtLen(tertiary), issue: issueLabel, size: primaryMatchName, range: this.#fmtLenRange(primaryMatch[tk].min, primaryMatch[tk].max) }),
            note: noteText,
            contactFallback: true,
            growthNotice,
          });
        }
      } else {
        this.#showResult({
          type: 'warning',
          title: this.#t('result_recommend').replace('{{ size }}', primaryMatchName),
          size: primaryMatchName,
          message: this.#t('msg_tertiary_slight', { size: primaryMatchName, primary_name: primaryName, tertiary_name: tertiaryName, issue: slightlyLabel, range: this.#fmtLenRange(primaryMatch[tk].min, primaryMatch[tk].max) }),
          note: noteText,
          selectVariant: primaryMatchName,
          growthNotice,
        });
      }

      return;
    }

    if (weightOutOfBreedRange) {
      if (allPerfectFits.length > 1) {
        const sorted = this.#sortSizes(allPerfectFits, sizes);
        const smaller = sorted[0];
        const larger = sorted[sorted.length - 1];
        const sizesList = this.#joinSizes(sorted);
        let recommended;
        let msg;

        if (growthStage === 'growing' || growthStage === 'almost-mature') {
          recommended = larger;
          msg = this.#t('msg_multi_growing_weight', { sizes: sizesList, smaller, larger, breed, weight_message: weightRangeMessage });
        } else {
          recommended = smaller;
          msg = this.#t('msg_multi_fit_weight', { sizes: sizesList, smaller, larger, breed, weight_message: weightRangeMessage });
        }

        this.#showResult({
          type: 'warning',
          title: this.#t('result_recommend').replace('{{ size }}', recommended),
          size: recommended,
          message: msg,
          note: noteText,
          selectVariant: recommended,
          alternativeSizes: allPerfectFits,
          growthNotice,
        });
      } else {
        this.#showResult({
          type: 'warning',
          title: this.#t('result_recommend').replace('{{ size }}', primaryMatchName),
          size: primaryMatchName,
          message: this.#t('msg_fit_weight', { size: primaryMatchName, weight_message: weightRangeMessage }),
          note: noteText,
          selectVariant: primaryMatchName,
          growthNotice,
        });
      }

      return;
    }

    /* Multiple perfect fits */
    if (allPerfectFits.length > 1) {
      const sorted = this.#sortSizes(allPerfectFits, sizes);
      const smaller = sorted[0];
      const larger = sorted[sorted.length - 1];
      const sizesList = this.#joinSizes(sorted);
      let recommended;
      let msg;

      if (growthStage === 'growing' || growthStage === 'almost-mature') {
        recommended = larger;
        msg = this.#t('msg_multi_growing', { sizes: sizesList, smaller, larger, breed });
      } else if (growthStage === 'mature') {
        recommended = smaller;
        msg = this.#t('msg_multi_mature', { sizes: sizesList, smaller, larger, breed });
      } else {
        recommended = primaryMatchName;
        msg = this.#t('msg_multi_options', { sizes: sizesList, smaller, larger, breed });
      }

      let title = this.#t('result_recommend').replace('{{ size }}', recommended);

      if (sizedUpForComfort) {
        title = this.#t('result_sized_up');
        msg = this.#t('msg_comfort_sizeup', { measurement: comfortSnug.join(' and '), size: recommended, breed });
      }

      this.#showResult({
        type: 'success',
        title,
        size: recommended,
        message: msg,
        note: noteText,
        selectVariant: recommended,
        alternativeSizes: allPerfectFits,
        growthNotice,
      });
    } else {
      /* Single perfect fit */
      let type = 'success';
      let title = this.#t('result_perfect');
      let msg = this.#t('msg_perfect_fit', { size: primaryMatchName, breed });

      if (sizedUpForComfort) {
        title = this.#t('result_sized_up');
        msg = this.#t('msg_comfort_sizeup', { measurement: comfortSnug.join(' and '), size: primaryMatchName, breed });
      } else if (comfortAdvisory) {
        type = 'warning';
        title = this.#t('result_recommend').replace('{{ size }}', primaryMatchName);
        msg = this.#t('msg_comfort_snug_edge', { measurement: comfortSnug.join(' and '), size: primaryMatchName, breed });
      }

      if (growthStage === 'growing' || growthStage === 'almost-mature') {
        msg += ` ${this.#t('msg_perfect_fit_growing')}`;
      }

      this.#showResult({
        type,
        title,
        size: primaryMatchName,
        message: msg,
        note: noteText,
        selectVariant: primaryMatchName,
        growthNotice,
      });
    }
  }

  #sortSizes(sizeNames, sizes) {
    const pk = this.#primaryKey;

    return sizeNames.slice().sort((a, b) => {
      const aMin = sizes[a] && sizes[a][pk] ? sizes[a][pk].min : 0;
      const bMin = sizes[b] && sizes[b][pk] ? sizes[b][pk].min : 0;
      return aMin - bMin;
    });
  }

  /* ==========================================================================
     Result rendering
     ========================================================================== */

  #stockBadgeHtml(sizeName, stock, attrs = '') {
    if (stock === null) {
      return ` <span class="size-quiz__stock-badge size-quiz__stock-badge--oos"${attrs}>${this.#t('not_available')}</span>`;
    }

    if (stock === false) {
      return ` <span class="size-quiz__stock-badge size-quiz__stock-badge--oos"${attrs}>${this.#t('out_of_stock')}</span>`;
    }

    if (stock === true) {
      return ` <span class="size-quiz__stock-badge size-quiz__stock-badge--in-stock"${attrs}>${this.#t('in_stock')}</span>`;
    }

    return ` <span class="size-quiz__stock-badge size-quiz__stock-badge--loading"${attrs}></span>`;
  }

  #showResult(result) {
    this.#form.classList.add('size-quiz__hidden');
    this.#resultContainer.classList.remove('size-quiz__hidden');
    this.#submitBtn.textContent = this.#t('update_details');

    /* Icon */
    const iconClass =
      result.type === 'success' ? 'success' : result.type === 'warning' ? 'warning' : 'error';
    const iconSvg = this.#getResultIcon(result.type);

    /* Size display */
    let sizeHtml = '';

    if (result.size) {
      if (this.#viewProductOnly) {
        sizeHtml = `<div class="size-quiz__result-size">${this.#esc(result.size)}</div>`;
      } else {
        const stock = this.#getVariantStock(result.size);

        sizeHtml = `
        <div class="size-quiz__result-size">${this.#esc(result.size)}</div>
        <span class="size-quiz__primary-stock${stock === true ? ' size-quiz__stock-badge size-quiz__stock-badge--in-stock' : stock === false ? ' size-quiz__stock-badge size-quiz__stock-badge--oos' : ' size-quiz__stock-badge size-quiz__stock-badge--loading'}" data-quiz-primary-stock>${stock === true ? this.#t('in_stock') : stock === false ? this.#t('out_of_stock') : ''}</span>`;
      }
    }

    /* Note */
    const noteHtml = result.note ? `<p class="size-quiz__result-note">${result.note}</p>` : '';

    /* Growth notice */
    const growthHtml = result.growthNotice
      ? `<div class="size-quiz__growth-notice">
          ${this.#iconHtml('info')}
          <p>${result.growthNotice}</p>
        </div>`
      : '';

    /* Actions */
    let actionsHtml = '<div class="size-quiz__actions">';

    if (this.#viewProductOnly) {
      if (this.#viewProductUrl) {
        actionsHtml += `<a class="size-quiz__action-btn size-quiz__action-btn--link js-quiz-view-product" href="${this.#esc(this.#viewProductUrl)}">${this.#t('view_product')}</a>`;
      }
    } else if (result.alternativeSizes && result.alternativeSizes.length > 1) {
      for (const sizeName of result.alternativeSizes) {
        const btnStock = this.#getVariantStock(sizeName);
        const btnOos = btnStock === false || btnStock === null ? ' size-quiz__action-btn--oos' : '';
        const btnDisabled = btnStock === null ? ' disabled' : '';
        const badge = this.#stockBadgeHtml(sizeName, btnStock, ` data-quiz-btn-stock="${sizeName}"`);

        actionsHtml += `<button type="button" class="size-quiz__action-btn js-quiz-select-size${btnOos}" data-size="${sizeName}"${btnDisabled}>${this.#t('select_size').replace('{{ size }}', sizeName)}${badge}</button>`;
        actionsHtml += `<div class="quiz-btn-colour-notice" data-colour-notice-size="${sizeName}"></div>`;
      }
    } else if (result.selectVariant) {
      const svStock = this.#getVariantStock(result.selectVariant);
      const svOos = svStock === false || svStock === null ? ' size-quiz__action-btn--oos' : '';
      const svDisabled = svStock === null ? ' disabled' : '';
      const badge = this.#stockBadgeHtml(result.selectVariant, svStock, ` data-quiz-btn-stock="${result.selectVariant}"`);

      actionsHtml += `<button type="button" class="size-quiz__action-btn js-quiz-select-size${svOos}" data-size="${result.selectVariant}"${svDisabled}>${this.#t('select_size').replace('{{ size }}', result.selectVariant)}${badge}</button>`;
    }

    /* Alternatives */
    if (!this.#viewProductOnly && result.alternatives && result.alternatives.length > 0) {
      for (const alt of result.alternatives) {
        this.#colourMatchAlternative(alt);
      }

      actionsHtml += `<div class="size-quiz__alternatives"><p class="size-quiz__alt-label">${this.#t('we_suggest')}</p>`;

      let idx = 0;

      for (const alt of result.alternatives) {
        let altUrl = alt.productUrl || `/collections/${alt.handle}`;
        if (alt.variantId) altUrl += `${altUrl.includes('?') ? '&' : '?'}variant=${alt.variantId}`;

        const sizeLabel = alt.fittingSize ? ` - Size ${alt.fittingSize}` : '';
        const stockBadge = `<span id="quiz-alt-stock-${idx}" class="size-quiz__stock-badge size-quiz__stock-badge--loading"></span>`;

        actionsHtml += `
          <a href="${altUrl}" class="size-quiz__alt-card" data-alt-idx="${idx}">
            <span class="size-quiz__alt-card-info">
              <span class="size-quiz__alt-card-name">${this.#esc(alt.name)}</span>
              <span class="size-quiz__alt-card-size">${sizeLabel} ${stockBadge}</span>
            </span>
            ${this.#iconHtml('chevron')}
          </a>
          <div id="quiz-alt-colours-${idx}" class="size-quiz__alt-colours"></div>`;

        idx++;
      }

      actionsHtml += '</div>';
    }

    /* OOS notice container */
    actionsHtml += '<div id="quiz-oos-notice"></div>';

    /* Flexi alternative CTA (muzzle only, primary measurement OOR) */
    if (result.flexiCta) {
      const flexi = this.#config.flexiAlternative;

      if (flexi && flexi.enabled && flexi.url) {
        actionsHtml += `
          <div class="size-quiz__alternatives size-quiz__flexi">
            <p class="size-quiz__alt-label">${this.#t('flexi_heading')}</p>
            <p class="size-quiz__flexi-body">${this.#t('flexi_body')}</p>
            <a href="${flexi.url}" class="size-quiz__alt-card">
              <span class="size-quiz__alt-card-info">
                <span class="size-quiz__alt-card-name">${this.#esc(flexi.title)}</span>
                <span class="size-quiz__alt-card-size">${this.#t('flexi_cta_label')}</span>
              </span>
              ${this.#iconHtml('chevron')}
            </a>
          </div>`;
      }
    }

    /* Contact fallback — Help Center page */
    if (result.contactFallback && this.#helpUrl) {
      actionsHtml += `
        <div class="size-quiz__contact">
          <p class="size-quiz__alt-label">${this.#t('need_help')}</p>
          <a class="size-quiz__contact-link" href="${this.#esc(this.#helpUrl)}">
            ${this.#t('contact_us')}
            ${this.#iconHtml('chevron')}
          </a>
        </div>`;
    }

    actionsHtml += '</div>';

    this.#resultContainer.innerHTML = `
      <div class="size-quiz__result-icon size-quiz__result-icon--${iconClass}">${iconSvg}</div>
      <h3 class="size-quiz__result-title">${result.title}</h3>
      ${sizeHtml}
      <p class="size-quiz__result-message">${result.message}</p>
      ${growthHtml}
      ${noteHtml}
      ${actionsHtml}`;

    /* Bind select buttons */
    for (const btn of this.#resultContainer.querySelectorAll('.js-quiz-select-size')) {
      btn.addEventListener('click', () => {
        const onProductPage = this.#isOnQuizProductPage();
        const selected = onProductPage ? this.#selectVariant(btn.dataset.size) : false;

        if (!selected) {
          this.#navigateToVariant(btn.dataset.size);
          return;
        }

        if (window.dockManager) window.dockManager.closeAll();
      });
    }

    /* Live stock check */
    this.#fetchLiveStock(result);
    this.#fetchAltStock(result);
  }

  #getResultIcon(type) {
    if (type === 'success') return this.#iconHtml('success');
    if (type === 'warning') return this.#iconHtml('warning');
    return this.#iconHtml('error');
  }

  /* ==========================================================================
     Live stock updates
     ========================================================================== */

  #fetchLiveStock(result) {
    if (this.#viewProductOnly) return;
    const handle = this.#data.product && this.#data.product.handle;
    if (!handle) return;

    const sizesToCheck = result.alternativeSizes && result.alternativeSizes.length > 0
      ? result.alternativeSizes
      : result.size
        ? [result.size]
        : [];

    if (!sizesToCheck.length && !result.selectVariant) return;

    this.#fetchProductStock(handle).then((variants) => {
      /* Update primary badge */
      const primaryBadge = this.#resultContainer.querySelector('[data-quiz-primary-stock]');

      if (primaryBadge && result.size) {
        let match = null;

        for (const v of variants) {
          if (this.#sizeMatchesOption(v.option1, result.size)) {
            match = v;
            break;
          }
        }

        primaryBadge.classList.remove('size-quiz__stock-badge--loading');

        if (match && match.available) {
          primaryBadge.className = 'size-quiz__primary-stock size-quiz__stock-badge size-quiz__stock-badge--in-stock';
          primaryBadge.textContent = this.#t('in_stock');
        } else {
          primaryBadge.className = 'size-quiz__primary-stock size-quiz__stock-badge size-quiz__stock-badge--oos';
          primaryBadge.textContent = match ? this.#t('out_of_stock') : this.#t('not_available');

          /* Upgrade to In Stock if any group sibling carries this size. */
          this.#findGroupSiblingForSize(result.size).then((sibling) => {
            if (!sibling) return;
            primaryBadge.className = 'size-quiz__primary-stock size-quiz__stock-badge size-quiz__stock-badge--in-stock';
            primaryBadge.textContent = this.#t('in_stock');
          });
        }
      }

      /* Update button badges */
      for (const badge of this.#resultContainer.querySelectorAll('[data-quiz-btn-stock]')) {
        const sz = badge.getAttribute('data-quiz-btn-stock');
        let match = null;

        for (const v of variants) {
          if (this.#sizeMatchesOption(v.option1, sz)) {
            match = v;
            break;
          }
        }

        const repAvailable = Boolean(match && match.available);
        const parentBtn = badge.closest('.js-quiz-select-size');

        if (repAvailable) {
          badge.className = 'size-quiz__stock-badge size-quiz__stock-badge--in-stock';
          badge.textContent = this.#t('in_stock');
          if (parentBtn) parentBtn.classList.remove('size-quiz__action-btn--oos');
        } else {
          /* Mark OOS for now — may be upgraded once group sibling stock resolves */
          badge.className = 'size-quiz__stock-badge size-quiz__stock-badge--oos';
          badge.textContent = match ? this.#t('out_of_stock') : this.#t('not_available');
          if (parentBtn) parentBtn.classList.add('size-quiz__action-btn--oos');
        }

        /* Resolve all in-stock options across rep + group siblings to power
           the colour-choice swatch row beside the button. */
        this.#findAllGroupSiblingsForSize(sz).then((siblingMatches) => {
          const options = [];

          if (repAvailable) {
            options.push({
              colour: (this.#data.product && this.#data.product.colour) || (this.#data.product && this.#data.product.title) || '',
              url: (this.#data.product && this.#data.product.url) || '',
              variantId: match ? match.id : null,
              swatchColour: (this.#data.product && this.#data.product.swatchColour) || '',
              swatchImage: (this.#data.product && this.#data.product.swatchImage) || null,
            });
          }

          for (const sib of siblingMatches) options.push(sib);

          /* No options anywhere — leave existing OOS state. */
          if (!options.length) return;

          /* If at least one sibling has it (and rep didn't), upgrade badge. */
          if (!repAvailable && siblingMatches.length) {
            badge.className = 'size-quiz__stock-badge size-quiz__stock-badge--in-stock';
            badge.textContent = this.#t('in_stock');
            if (parentBtn) {
              parentBtn.classList.remove('size-quiz__action-btn--oos');
              parentBtn.disabled = false;
            }
          }

          /* Only render swatches if there's a real choice to make. */
          if (options.length < 2) return;

          const noticeTarget = this.#resultContainer.querySelector(`[data-colour-notice-size="${sz}"]`);
          if (!noticeTarget) return;

          noticeTarget.innerHTML = this.#buildColourChoice(options);
          this.#bindSwatchHovers(noticeTarget);
        });
      }

      /* Check colour siblings for OOS sizes */
      const oosSizes = [];

      for (const sz of sizesToCheck) {
        let match = null;

        for (const v of variants) {
          if (this.#sizeMatchesOption(v.option1, sz)) {
            match = v;
            break;
          }
        }

        if (!match || !match.available) oosSizes.push(sz);
      }

      for (const oosSize of oosSizes) {
        this.#checkColourSiblingStock(oosSize).then((colourResults) => {
          const available = colourResults.filter((c) => c.available);
          if (!available.length) return;

          const noticeHtml = this.#buildColourNotice(available, oosSize);
          const target =
            this.#resultContainer.querySelector(`[data-colour-notice-size="${oosSize}"]`) ||
            document.getElementById('quiz-oos-notice');
          if (target) target.innerHTML = noticeHtml;
          if (target) this.#bindSwatchHovers(target);
        });
      }
    });
  }

  #fetchAltStock(result) {
    if (this.#viewProductOnly) return;
    if (!result.alternatives || !result.alternatives.length) return;

    let idx = 0;

    for (const alt of result.alternatives) {
      const currentIdx = idx++;

      if (!alt.productHandle || !alt.fittingSize) {
        const badge = document.getElementById(`quiz-alt-stock-${currentIdx}`);
        if (badge) badge.classList.add('size-quiz__hidden');
        continue;
      }

      this.#fetchProductStock(alt.productHandle).then((variants) => {
        const badge = document.getElementById(`quiz-alt-stock-${currentIdx}`);
        if (!badge) return;

        let match = null;

        for (const v of variants) {
          if (this.#sizeMatchesOption(v.option1, alt.fittingSize)) {
            match = v;
            break;
          }
        }

        badge.classList.remove('size-quiz__stock-badge--loading');

        if (match && match.available) {
          badge.className = 'size-quiz__stock-badge size-quiz__stock-badge--in-stock';
          badge.textContent = this.#t('in_stock');
        } else if (match) {
          badge.className = 'size-quiz__stock-badge size-quiz__stock-badge--oos';
          badge.textContent = this.#t('out_of_stock');

          this.#checkAltColourSiblingStock(alt.refHandle || alt.productHandle, alt.productHandle, alt.fittingSize).then(
            (colourResults) => {
              const container = document.getElementById(`quiz-alt-colours-${currentIdx}`);
              if (!container) return;

              const available = colourResults.filter((c) => c.available);
              if (!available.length) return;

              container.innerHTML = this.#buildAltColourNotice(available, alt);
              this.#bindSwatchHovers(container);
            }
          );
        } else {
          badge.classList.add('size-quiz__hidden');
        }
      });
    }
  }

  /* ==========================================================================
     Colour notice builders
     ========================================================================== */

  #buildSwatchList(availableColours, currentSwatchColour, currentSwatchImage) {
    let currentStyle = `--swatch-color:${currentSwatchColour || '#ccc'}`;
    if (currentSwatchImage) currentStyle += `;--swatch-image:url(${currentSwatchImage})`;

    let html = `<li class="size-quiz__swatch-item size-quiz__swatch-item--current"><span class="size-quiz__swatch-circle" style="${currentStyle}" role="img" aria-hidden="true"></span></li>`;

    for (const c of availableColours) {
      let style = `--swatch-color:${c.swatchColour || '#ccc'}`;
      if (c.swatchImage) style += `;--swatch-image:url(${c.swatchImage})`;

      let url = c.url;
      if (c.variantId) url += `${url.includes('?') ? '&' : '?'}variant=${c.variantId}`;

      html += `<li class="size-quiz__swatch-item size-quiz__swatch-item--in-stock"><a href="${url}" class="size-quiz__swatch-circle" style="${style}" title="${this.#esc(c.colour)} (${this.#t('in_stock')})" aria-label="${this.#esc(c.colour)} - ${this.#t('in_stock')}" data-colour="${this.#esc(c.colour)}"></a></li>`;
    }

    return html;
  }

  #buildColourNotice(availableColours, oosSize) {
    const product = this.#data.product || {};
    const swatchesHtml = this.#buildSwatchList(availableColours, product.swatchColour, product.swatchImage);

    return `
      <div class="size-quiz__colour-notice">
        <p class="size-quiz__colour-label">${this.#t('available_colours')}</p>
        <ul class="size-quiz__swatch-list">${swatchesHtml}</ul>
        <div class="size-quiz__swatch-name-row"><span class="size-quiz__swatch-name" data-default="${this.#esc(product.colour || '')}">${this.#esc(product.colour || '')}</span></div>
      </div>`;
  }

  /**
   * Renders a colour-picker swatch row beside an in-stock size button when
   * multiple products in the group have it available. Used for the breed-LP
   * carousel where the "rep" product may be OOS but its group siblings are not.
   * @param {Array} options Each: `{ colour, url, variantId, swatchColour, swatchImage }`
   */
  #buildColourChoice(options) {
    let swatchesHtml = '';

    for (const c of options) {
      let style = `--swatch-color:${c.swatchColour || '#ccc'}`;
      if (c.swatchImage) style += `;--swatch-image:url(${c.swatchImage})`;

      let url = c.url || '';
      if (url && c.variantId) url += `${url.includes('?') ? '&' : '?'}variant=${c.variantId}`;

      swatchesHtml += `<li class="size-quiz__swatch-item size-quiz__swatch-item--in-stock"><a href="${url}" class="size-quiz__swatch-circle" style="${style}" title="${this.#esc(c.colour)}" aria-label="${this.#esc(c.colour)}" data-colour="${this.#esc(c.colour)}"></a></li>`;
    }

    const defaultColour = options[0] ? options[0].colour : '';

    return `
      <div class="size-quiz__colour-notice size-quiz__colour-notice--choice">
        <p class="size-quiz__colour-label">${this.#t('choose_colour')}</p>
        <ul class="size-quiz__swatch-list">${swatchesHtml}</ul>
        <div class="size-quiz__swatch-name-row"><span class="size-quiz__swatch-name" data-default="${this.#esc(defaultColour)}">${this.#esc(defaultColour)}</span></div>
      </div>`;
  }

  #buildAltColourNotice(availableColours, alt) {
    const swatchesHtml = this.#buildSwatchList(availableColours, alt.displaySwatchColour || '#000', alt.displaySwatchImage);

    return `
      <p class="size-quiz__colour-label">${this.#t('alt_colours')}</p>
      <ul class="size-quiz__swatch-list">${swatchesHtml}</ul>
      <div class="size-quiz__swatch-name-row"><span class="size-quiz__swatch-name" data-default="${this.#esc(alt.displayColour || '')}">${this.#esc(alt.displayColour || '')}</span></div>`;
  }

  #bindSwatchHovers(container) {
    const nameEl = container.querySelector('.size-quiz__swatch-name');
    if (!nameEl) return;

    const defaultName = nameEl.getAttribute('data-default') || '';

    for (const item of container.querySelectorAll('.size-quiz__swatch-item')) {
      item.addEventListener('mouseenter', () => {
        const link = item.querySelector('a[data-colour]');

        if (link) {
          nameEl.textContent = link.getAttribute('data-colour');
        } else if (item.classList.contains('size-quiz__swatch-item--current')) {
          nameEl.textContent = `${defaultName} (${this.#t('out_of_stock')})`;
        }
      });

      item.addEventListener('mouseleave', () => {
        nameEl.textContent = defaultName;
      });
    }
  }

  /* ==========================================================================
     Variant selection
     ========================================================================== */

  #selectVariant(sizeName) {
    const variants = this.#data.variants || [];

    for (const variant of variants) {
      if (!this.#sizeMatchesOption(variant.option1, sizeName)) continue;

      /* Try clicking a radio input */
      const radio = document.querySelector(`input[type="radio"][value="${variant.option1}"]`);

      if (radio) {
        radio.click();
        return true;
      }

      /* Try clicking an option button */
      const button = document.querySelector(`[data-option-value="${variant.option1}"]`);

      if (button) {
        button.click();
        return true;
      }

      /* Try clicking a label for the input */
      const label = document.querySelector(`label[for*="${variant.option1}"]`);

      if (label) {
        label.click();
        return true;
      }
    }

    return false;
  }

  /**
   * Fallback for standalone contexts (size-guide sections, collection pages,
   * etc.) where no variant selector exists on the page. Navigates to the
   * quiz product's PDP with the matching variant preselected via `?variant=`.
   *
   * If the rep product is OOS at the requested size and a group sibling has
   * it in stock, navigates to that sibling instead.
   */
  #navigateToVariant(sizeName) {
    const product = this.#data.product || {};
    const baseUrl = product.url;

    if (!baseUrl) return;

    const variants = this.#data.variants || [];
    let variantId = null;
    let variantAvailable = false;

    for (const variant of variants) {
      if (this.#sizeMatchesOption(variant.option1, sizeName)) {
        variantId = variant.id;
        variantAvailable = Boolean(variant.available);
        break;
      }
    }

    /* If rep has it in stock (per initial JSON), go straight there. */
    if (variantId && variantAvailable) {
      const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
      window.location.href = url;
      return;
    }

    /* Otherwise look for an in-stock group sibling. */
    this.#findGroupSiblingForSize(sizeName).then((sibling) => {
      if (sibling && sibling.url) {
        const url = sibling.variantId
          ? `${sibling.url}${sibling.url.includes('?') ? '&' : '?'}variant=${sibling.variantId}`
          : sibling.url;
        window.location.href = url;
        return;
      }

      /* Fall back to rep product PDP (variant preselected if known). */
      const url = variantId ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variantId}` : baseUrl;
      window.location.href = url;
    });
  }

  /**
   * Returns true if the current page is the quiz product's own PDP. Used to
   * decide whether the "Select size" button should wire into an on-page
   * variant selector or navigate to the product page.
   */
  #isOnQuizProductPage() {
    const product = this.#data.product || {};
    if (!product.url) return false;

    /* product.url from Liquid is the relative path (e.g. "/products/foo"). */
    const target = product.url.split('?')[0].replace(/\/$/, '');
    const current = window.location.pathname.replace(/\/$/, '');

    return current === target;
  }

  /* ==========================================================================
     Form / result toggles
     ========================================================================== */

  #showForm() {
    this.#form.classList.remove('size-quiz__hidden');
    this.#resultContainer.classList.add('size-quiz__hidden');
    this.#submitBtn.textContent = this.#t('find_my_size');
  }

  #reset() {
    if (this.#breedSelect) this.#breedSelect.value = '';
    if (this.#ageSelect) this.#ageSelect.value = '';
    if (this.#weightInput) this.#weightInput.value = '';

    for (const input of this.#measurementInputs.values()) {
      if (input) input.value = '';
    }

    this.#showForm();
    this.#updateFieldVisibility();
  }

  /* ==========================================================================
     Helpers
     ========================================================================== */

  #getMeasurementDisplayName(key) {
    const m = this.#measurements.get(key);
    return m && m.displayName ? m.displayName : key;
  }

  #capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Joins a list of size names into a human-readable string.
   * Examples: ['C'] → 'C'; ['C','E'] → 'C and E'; ['C','D','E'] → 'C, D and E'.
   */
  #joinSizes(sizesArr) {
    if (!Array.isArray(sizesArr) || sizesArr.length === 0) return '';
    if (sizesArr.length === 1) return sizesArr[0];
    if (sizesArr.length === 2) return `${sizesArr[0]} and ${sizesArr[1]}`;
    return `${sizesArr.slice(0, -1).join(', ')} and ${sizesArr[sizesArr.length - 1]}`;
  }

  /* ==========================================================================
     i18n helper
     ========================================================================== */

  #t(key, replacements) {
    const translations = this.#config.translations || {};
    let text = translations[key] || key;

    if (replacements) {
      for (const [k, v] of Object.entries(replacements)) {
        text = text.replaceAll(`{{ ${k} }}`, v);
      }
    }

    return text;
  }

  #esc(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  #iconHtml(name) {
    return this.#iconTemplates[name] || '';
  }
}

  window.SizeQuiz = SizeQuiz;
}

/* ==========================================================================
   Auto-init
   Runs on every script load (the class declaration above is guarded so the
   listener below is the only thing that re-binds when the script tag is
   re-injected, e.g. on section reload in the theme editor).

   On product templates, quizzes inside the PDP size guide dock-modal are deferred
   until the buyer opens that modal (see layout/theme.liquid `template-product`).
   ========================================================================== */
const isProductTemplate = () => document.body.classList.contains('template-product');

const isDeferredProductSizeGuideQuiz = (el) =>
  isProductTemplate() && Boolean(el.closest('dock-modal[id^="SizeGuideModal-"]'));

const initSizeQuizElement = (el) => {
  if (el.dataset.sizeQuizInit === 'true') {
    return;
  }

  el.dataset.sizeQuizInit = 'true';
  new window.SizeQuiz(el);
};

window.initSizeQuizElement = initSizeQuizElement;

const initSizeQuizzes = () => {
  for (const el of document.querySelectorAll('.js-size-quiz')) {
    if (isDeferredProductSizeGuideQuiz(el)) {
      continue;
    }

    initSizeQuizElement(el);
  }
};

document.addEventListener(
  'click',
  (e) => {
    const trigger = e.target.closest('.js-size-guide-trigger');
    if (!trigger || !isProductTemplate()) {
      return;
    }

    const targetId = trigger.getAttribute('data-target');
    if (!targetId || !targetId.startsWith('SizeGuideModal-')) {
      return;
    }

    const modal = document.getElementById(targetId);
    if (!modal) {
      return;
    }

    window.requestAnimationFrame(() => {
      for (const el of modal.querySelectorAll('.js-size-quiz')) {
        initSizeQuizElement(el);
      }
    });
  },
  true,
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeQuizzes);
} else {
  initSizeQuizzes();
}
